import ConfirmModal from "../../Modal/ConfirmModal"
import { QuestionStatus, VerificationStatus } from "../../../api/type"

type VerificationDecision = Exclude<VerificationStatus, "unverified">

type QuestionConfirmModalsProps = {
  confirmType: "delete" | "publish" | "status" | "learner" | "verify" | null
  pendingStatus?: QuestionStatus | null
  pendingLearnerValue?: boolean | null
  pendingVerification?: VerificationDecision | null
  isLoading: boolean
  onConfirm: () => void
  onCancel: () => void
}

const QuestionConfirmModals = ({
  confirmType,
  pendingStatus = null,
  pendingLearnerValue = null,
  pendingVerification = null,
  isLoading,
  onConfirm,
  onCancel,
}: QuestionConfirmModalsProps) => {
  const learnerModal =
    confirmType === "learner"
      ? pendingLearnerValue
        ? {
            title: "Publish to learners",
            message:
              "Publish this question to learners? It will become visible to learners.",
            confirmLabel: "Publish to learners",
          }
        : {
            title: "Remove from learners",
            message:
              "Remove this question from learners? It will no longer be visible to learners.",
            confirmLabel: "Remove from learners",
          }
      : null

  const statusModal =
    confirmType === "status" && pendingStatus
      ? pendingStatus === "archived"
        ? {
            title: "Archive question",
            message:
              "Archive this question? It will be hidden from published lists but can be unarchived later.",
            confirmLabel: "Archive",
          }
        : pendingStatus === "draft"
          ? {
              title: "Move to draft",
              message:
                "Move this question back to draft? It will no longer be published.",
              confirmLabel: "Move to draft",
            }
          : {
              title: "Unarchive question",
              message:
                "Unarchive this question? It will be published and visible in the library again.",
              confirmLabel: "Unarchive",
            }
      : null

  const verifyModal =
    confirmType === "verify"
      ? pendingVerification === "rejected"
        ? {
            title: "Reject question",
            message:
              "Reject this AI-generated question? It will stay out of exam papers until verified.",
            confirmLabel: "Reject",
          }
        : {
            title: "Verify question",
            message:
              "Mark this AI-generated question as verified? You can then publish it to the bank.",
            confirmLabel: "Verify",
          }
      : null

  return (
    <>
      <ConfirmModal
        isOpen={confirmType === "delete"}
        title="Delete question"
        message="Are you sure you want to delete this question? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isLoading={isLoading}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
      <ConfirmModal
        isOpen={confirmType === "publish"}
        title="Publish question"
        message="Publish this question? It will be published and visible in the library."
        confirmLabel="Publish"
        cancelLabel="Cancel"
        isLoading={isLoading}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
      <ConfirmModal
        isOpen={Boolean(statusModal)}
        title={statusModal?.title ?? "Update status"}
        message={statusModal?.message ?? "Update this question status?"}
        confirmLabel={statusModal?.confirmLabel ?? "Confirm"}
        cancelLabel="Cancel"
        isLoading={isLoading}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
      <ConfirmModal
        isOpen={Boolean(learnerModal)}
        title={learnerModal?.title ?? "Update learner visibility"}
        message={
          learnerModal?.message ?? "Update this question's learner visibility?"
        }
        confirmLabel={learnerModal?.confirmLabel ?? "Confirm"}
        cancelLabel="Cancel"
        isLoading={isLoading}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
      <ConfirmModal
        isOpen={Boolean(verifyModal)}
        title={verifyModal?.title ?? "Verify question"}
        message={verifyModal?.message ?? "Update verification for this question?"}
        confirmLabel={verifyModal?.confirmLabel ?? "Confirm"}
        cancelLabel="Cancel"
        isLoading={isLoading}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    </>
  )
}

export default QuestionConfirmModals
